import React, { useState } from "react";
import { Flex, Tag, theme } from "antd";
import { DateField } from "@refinedev/antd";
import { CloseCircleOutlined } from "@ant-design/icons";
import { useLocalStorage } from "@/hooks/useLocalStorage";

interface FilterTagsProps {
  searchFormProps: any;
  onSearch: any;
  i18n?: any;
  filters?: any;
  setFilters?: any;
  storesSelectProps?: any;
  t?: any;
  onDeleteFilter?: any;
  selectedContact?: any;
  customerContact?: any;
  userContactId?: number[] | null;
}

import {
  parseCustomersFilterValue,
  parseOperatorsFilterValue,
  type CustomerSelection,
  type OperatorAffiliationSelection,
} from "./people";
import {
  getFieldLabel,
  excludedFields,
  fieldClearers,
  getDisplayValue,
  isBooleanLabelOnlyField,
} from "./filtersUtils";

export const FilterTags: React.FC<FilterTagsProps> = ({
  i18n,
  storesSelectProps,
  searchFormProps,
  onSearch,
  filters,
  setFilters,
  onDeleteFilter,
  t,
  selectedContact,
  customerContact,
  userContactId,
}) => {
  const [radioValue, setRadioValue] = useLocalStorage<any>("RadioCalendar", []);
  const [radioValueCreated, setRadioValueCreated] = useLocalStorage<any>(
    "RadioCalendarCreated",
    [],
  );

  const { token } = theme.useToken();

  const presetGuardado = (clave: string): string => {
    try {
      return JSON.parse(window.localStorage.getItem(clave) ?? '""') || "";
    } catch {
      return "";
    }
  };

  const virtualToRealFields = {
    storeStatus: "storeLinkedSellers.active",
    linkStatus: "storeLinkedSellerRequests.active",
    requestStatus: "storeLinkedSellerRequests.active",
  };

  const deleteTagValue = (virtualField: string, valueToRemove?: string) => {
    const realField =
      virtualToRealFields[virtualField as keyof typeof virtualToRealFields] ??
      virtualField;

    let newFilters = filters.filter(
      (filter: any) => filter.field !== realField,
    );

    if (valueToRemove) {
      newFilters = filters
        .map((filter: any) => {
          if (filter.field === realField && Array.isArray(filter.value)) {
            return {
              ...filter,
              value: filter.value.filter((v: string) => v !== valueToRemove),
            };
          }
          return filter;
        })
        .filter(
          (filter: any) =>
            !(filter.field === realField && filter.value.length === 0),
        );
    } else {
      newFilters = filters.filter((filter: any) => filter.field !== realField);
    }

    const clearFields = fieldClearers[
      virtualField as keyof typeof fieldClearers
    ] ?? [virtualField];
    clearFields.forEach((f) => {
      searchFormProps.form?.setFieldsValue({ [f]: undefined });
    });

    if (virtualField === "participations.contactId") {
      // Clearing the contact-relation tag (manual select via the dropdown).
      // The "Soy participante" toggle now lives under participations.userId.
      searchFormProps.form?.setFieldsValue({ contactRelation: undefined, contactRelationName: undefined });
    }

    if (virtualField === "participations.userId") {
      // Clearing the "Soy participante" tag: reset the radio-driven form
      // field so the toggle UI goes back to "Todos".
      searchFormProps.form?.setFieldsValue({ participant_toggle: undefined });
    }

    if (virtualField === "archived") {
      // El filtro Status por defecto es "active" (no undefined): al quitar el
      // chip el select vuelve al default, no a vacío.
      searchFormProps.form?.setFieldsValue({ archived_status: "active" });
    }

    if (virtualField === "createdAt") {
      setRadioValueCreated("");
    }

    if (virtualField === "updatedAt") {
      setRadioValue("");
    }

    onSearch({
      ...searchFormProps.form?.getFieldsValue(),
      filters: newFilters,
    });

    setFilters(newFilters, "replace");
  };

  const reverseVirtualFields = {
    "storeLinkedSellers.active": "storeStatus",
    "storeLinkedSellerRequests.active": "linkStatus",
  };

  const dateRangeFields = [
    "createdAt",
    "updatedAt",
    "calendar",
    "calendar_created",
  ];

  const formatDate = (date: any) => (
    <DateField
      locales={"es"}
      value={date}
      format="LLL"
      style={{ color: token.colorTextSecondary }}
    />
  );

  const activeFilters =
    filters
      ?.filter(
        (filter: any) =>
          !excludedFields.includes(filter.field) &&
          filter.value !== undefined &&
          filter.value !== null &&
          filter.value !== "",
      ) ?? [];

  const handleClearAll = () => {

    searchFormProps.form?.resetFields?.();

    setRadioValue("");
    setRadioValueCreated("");

    const formValues = searchFormProps.form?.getFieldsValue?.() ?? {};

    // Wipe out participant_toggle and anything else related
    formValues.participant_toggle = undefined;
    formValues.contactRelation = undefined;
    formValues.contactRelationName = undefined;

    onSearch?.({
      ...formValues,
      filters: [],
    });

    setFilters?.([], "replace");
    onSearch?.({ ...formValues, filters: [] });
  };


  return (
    <Flex className="filter-tags">
      {filters
        ?.filter(
          (filter: any) =>
            !excludedFields.includes(filter.field) &&
            filter.value !== undefined &&
            filter.value !== null &&
            filter.value !== "",
        )
        .map((filter: any, index: number) => {
          const virtualField =
            reverseVirtualFields[
            filter.field as keyof typeof reverseVirtualFields
            ] || filter.field;
          const field = virtualField;
          const value = filter.value;

          if (field === "customerId") {
            return (
              <Tag
                key={index}
                closable
                onClose={() => deleteTagValue(field)}
                closeIcon={<CloseCircleOutlined />}
                aria-label={`Remove filter for ${field}`}
              >
                {getFieldLabel(t, field)}:{" "}
                <span>{customerContact ?? value}</span>
              </Tag>
            );
          }

          if (
            field === "contactGroupRelation.contactGroup.name" &&
            Array.isArray(value)
          ) {
            return value.map((group: string) => (
              <Tag
                key={`${field}-${group}`}
                closable
                onClose={() => deleteTagValue(field, group)}
                closeIcon={<CloseCircleOutlined />}
                aria-label={`Remove filter for ${field}: ${group}`}
              >
                {getFieldLabel(t, field)}: <span>{group}</span>
              </Tag>
            ));
          }

          return (
            <Tag
              key={index}
              closable
              onClose={() => deleteTagValue(field)}
              closeIcon={<CloseCircleOutlined />}
              aria-label={`Remove filter for ${field}`}
            >
              {Array.isArray(value) ? (
                field === "price" || field === "quoteSellers.total" ? (
                  <>
                    {`${t("filter.products.labelPriceFrom")} $${value[0] || 0}`}
                    {value[1] !== Number.MAX_VALUE
                      ? ` ${t("filter.products.labelPriceTo")} $${value[1]}`
                      : ` ${t("filter.products.labelPriceTo")} ${t(
                        "filter.products.labelPriceMax",
                      )}`}
                  </>
                ) : field === "quoteSellers.sellerCode" ? (
                  <>
                    {isBooleanLabelOnlyField(field) ? (
                      <>{getFieldLabel(t, field)}</>
                    ) : (
                      <>
                        {getFieldLabel(t, field)}:{" "}
                        <span>
                          {getDisplayValue(t, field, value, storesSelectProps)}
                        </span>
                      </>
                    )}
                  </>
                ) : field === "participations.userId" ? (
                  // "Soy participante" always renders as the labeled tag —
                  // the filter sends the user's own userId, no need for a
                  // value-derived label. Uses filter.participant.me ("Soy
                  // participante"); filter.contact.me is "Soy relación"
                  // (the contacts-page semantics) and would mislabel this tag.
                  <span>{t("filter.participant.me", "Soy participante")}</span>
                ) : field === "participations.contactId" ? (
                  <>
                    {(() => {
                      const isMe = value.some((v: any) => userContactId?.includes(Number(v)));
                      if (isMe) return <span>{t("filter.contact.me", "Soy participante")}</span>;

                      return (
                        <>
                          {getFieldLabel(t, field)}:{" "}
                          <span>{getDisplayValue(t, field, value, storesSelectProps)}</span>
                        </>
                      );
                    })()}
                  </>
                ) : (
                  <>
                    {dateRangeFields.includes(field) &&
                      Array.isArray(value) &&
                      value.length === 2 ? (
                      <>
                        {(() => {
                          // Lo que eligió el panel, leído ahora: el estado de este hook se
                          // carga una vez y no se entera de lo que el panel guarda después,
                          // así que "Esta semana" salía como rango (xcons-docs#609).
                          let presetValue = "";
                          if (field === "createdAt" || field === "calendar_created") {
                            presetValue = presetGuardado("RadioCalendarCreated");
                          } else if (field === "updatedAt" || field === "calendar") {
                            presetValue = presetGuardado("RadioCalendar");
                          }

                          if (presetValue === "today") return <>{getFieldLabel(t, field)}: <span>{t("filter.calendar.today")}</span></>;
                          if (presetValue === "thisweek") return <>{getFieldLabel(t, field)}: <span>{t("filter.calendar.thisWeek")}</span></>;
                          if (presetValue === "thismonth") return <>{getFieldLabel(t, field)}: <span>{t("filter.calendar.thisMonth")}</span></>;

                          return (
                            <>
                              {getFieldLabel(t, field)}:{" "}
                              <span>
                                Desde {formatDate(value[0])} Hasta {formatDate(value[1])}
                              </span>
                            </>
                          );
                        })()}
                      </>
                    ) : (
                      <>
                        {getFieldLabel(t, field)}:{" "}
                        <span>
                          {getDisplayValue(t, field, value, storesSelectProps)}
                        </span>
                      </>
                    )}
                  </>
                )
              ) : (
                <>
                  {field === "operators" ? (
                    (() => {
                      // Los nombres viven en el formulario; si se llegó por URL sólo hay ids.
                      const seleccion: OperatorAffiliationSelection[] | undefined =
                        searchFormProps.form?.getFieldValue("operators");
                      const items =
                        seleccion?.length ? seleccion : parseOperatorsFilterValue(value);
                      return (
                        <>
                          {getFieldLabel(t, field)}:{" "}
                          <span>
                            {items.map((s) => `${s.label} : ${s.personName}`).join(", ")}
                          </span>
                        </>
                      );
                    })()
                  ) : field === "customers" ? (
                    (() => {
                      const seleccion: CustomerSelection[] | undefined =
                        searchFormProps.form?.getFieldValue("customers");
                      const items =
                        seleccion?.length ? seleccion : parseCustomersFilterValue(value);
                      return (
                        <>
                          {getFieldLabel(t, field)}:{" "}
                          <span>{items.map((c) => c.name).join(", ")}</span>
                        </>
                      );
                    })()
                  ) : field === "participations.userId" ? (
                    <span>{t("filter.participant.me", "Soy participante")}</span>
                  ) : field === "participations.contactId" ? (
                    (() => {
                      const contactRelation = searchFormProps.form?.getFieldValue("contactRelation");
                      const contactName = searchFormProps.form?.getFieldValue("contactRelationName");
                      if (contactName && String(value) === String(contactRelation)) {
                        return (
                          <>
                            {getFieldLabel(t, field)}:{" "}
                            <span>{contactName}</span>
                          </>
                        );
                      }

                      if (userContactId?.includes(Number(value))) {
                        return <span>{t("filter.contact.me", "Soy participante")}</span>;
                      }

                      return (
                        <>
                          {getFieldLabel(t, field)}:{" "}
                          <span>
                            {getDisplayValue(t, field, value, storesSelectProps)}
                          </span>
                        </>
                      );
                    })()
                  ) : (
                    <>
                      {getFieldLabel(t, field)}:{" "}
                      <span>
                        {getDisplayValue(t, field, value, storesSelectProps)}
                      </span>
                    </>
                  )}
                </>
              )}
            </Tag>
          );
        })}

      {/* Tag global para limpiar todos los filtros */}
      {activeFilters.length > 0 && (
        <Tag
          onClick={handleClearAll}
          style={{ fontWeight: 500, backgroundColor: "#fff", borderRadius: 37, cursor: "pointer" }}
        >
          {t ? t("filter.button.clearFilters") : "Clear filters"}
        </Tag>
      )}
    </Flex>
  );
};
